import type React from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { useScrolling } from 'utils/hooks'
import { PopupUnderLayerSC } from './PopupUnderLayer.styled'

export interface IPopupUnderLayer {
  onClose?: () => void
  /** CSS class of toggle button; pointer/focus dismiss skipped to avoid toggle race (FP-4010) */
  toggleClassName?: string
  position?: 'fixed' | 'absolute' | 'relative'
  display?: string
  transform?: string
  top?: string
  left?: string
  bottom?: string
  right?: string
  children?: React.ReactNode
}

const stopEvent = (
  event?:
    | MouseEvent
    | KeyboardEvent
    | React.MouseEvent
    | FocusEvent
    | TouchEvent
) => {
  event?.stopPropagation()
  event?.preventDefault()
}

const focusWithoutScroll = (element: HTMLDivElement | null) => {
  if (!element) return

  try {
    element.focus({ preventScroll: true })
  } catch {
    element.focus()
  }
}

const isToggleTarget = (target: Node | null, toggleClassName?: string) =>
  Boolean(
    toggleClassName &&
    target instanceof HTMLElement &&
    target.closest(`.${toggleClassName}`)
  )

const shouldCloseOnPointer = (
  el: HTMLDivElement,
  target: Node,
  toggleClassName?: string
) =>
  !isToggleTarget(target, toggleClassName) &&
  (target === el || !el.contains(target))

export const PopupUnderLayer = ({
  onClose,
  toggleClassName,
  position,
  display,
  top,
  left,
  bottom,
  right,
  transform,
  children,
}: IPopupUnderLayer) => {
  const popupUnderLayerRef = useRef<HTMLDivElement>(null)

  useScrolling(popupUnderLayerRef)

  const handleEscButton = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        stopEvent(e)
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    const currentRef = popupUnderLayerRef.current

    if (currentRef) {
      currentRef.addEventListener('mouseup', stopEvent)
      return () => currentRef.removeEventListener('mouseup', stopEvent)
    }
  }, [])

  // focus popup for keyboard handling, but avoid browser auto-scroll/reflow
  useEffect(() => {
    focusWithoutScroll(popupUnderLayerRef.current)
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleEscButton)
    return () => document.removeEventListener('keydown', handleEscButton)
  }, [handleEscButton])

  const onGlobalBlur = useCallback(
    (e: FocusEvent) => {
      const el = popupUnderLayerRef.current
      const targetEl = (e.relatedTarget ||
        document.activeElement) as Node | null

      if (!targetEl || targetEl === document.body) {
        // focus is lost due to some unmounts in childs
        focusWithoutScroll(popupUnderLayerRef.current)
        return
      }

      if (isToggleTarget(targetEl, toggleClassName)) {
        return
      }

      // check if new active element is part of popup, if no -> close popup
      if (el && !el.contains(targetEl)) {
        stopEvent(e)
        onClose?.()
      }
    },
    [onClose, toggleClassName]
  )

  useEffect(() => {
    document.addEventListener('focusout', onGlobalBlur)
    return () => document.removeEventListener('focusout', onGlobalBlur)
  }, [onGlobalBlur])

  useEffect(() => {
    const onDocumentPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = popupUnderLayerRef.current
      if (!el || !onClose) return

      const target = e.target as Node

      if (!shouldCloseOnPointer(el, target, toggleClassName)) return

      stopEvent(e)
      onClose()
    }

    document.addEventListener('mousedown', onDocumentPointerDown, true)
    document.addEventListener('touchstart', onDocumentPointerDown, true)

    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown, true)
      document.removeEventListener('touchstart', onDocumentPointerDown, true)
    }
  }, [onClose, toggleClassName])

  return (
    <PopupUnderLayerSC
      onClick={stopEvent}
      ref={popupUnderLayerRef}
      tabIndex={0}
      position={position}
      display={display}
      transform={transform}
      top={top}
      left={left}
      bottom={bottom}
      right={right}
    >
      {children}
    </PopupUnderLayerSC>
  )
}
